import "../MuseoSansBinary.js"
import { createLetterPdf } from "../pdfUtils"

function getContainedLogoSize(imageWidth, imageHeight, maxWidth, maxHeight) {
  if (!imageWidth || !imageHeight) {
    return { width: maxWidth, height: maxHeight }
  }

  const widthRatio = maxWidth / imageWidth
  const heightRatio = maxHeight / imageHeight
  const scale = Math.min(widthRatio, heightRatio, 1)

  return {
    width: imageWidth * scale,
    height: imageHeight * scale,
  }
}

function drawReportLogo(doc, logoDataUri, imageWidth, imageHeight, x, y, maxWidth, maxHeight) {
  if (!logoDataUri) return

  try {
    const size = getContainedLogoSize(imageWidth, imageHeight, maxWidth, maxHeight)
    const drawX = x + (maxWidth - size.width) / 2
    const drawY = y + (maxHeight - size.height) / 2
    doc.addImage(logoDataUri, drawX, drawY, size.width, size.height)
  } catch (_err) {
    // Ignore logo rendering failures so report formatting remains stable.
  }
}

function formatFileName(meetName, meetDate) {
  const cleanName = (meetName || "MeetEntrySummary").replace(/[^a-zA-Z0-9]+/g, "_")
  const cleanDate = (meetDate || "").replace(/[^0-9-]+/g, "")
  if (cleanDate) {
    return `MeetEntrySummary_${cleanName}_${cleanDate}.pdf`
  }
  return `MeetEntrySummary_${cleanName}.pdf`
}

function formatGenderLabel(eventGender) {
  if (eventGender === "Girls" || eventGender === "Boys") return eventGender
  if (eventGender === 1 || eventGender === "1") return "Girls"
  if (eventGender === 2 || eventGender === "2") return "Boys"
  return "All"
}

export default function generateMeetEntrySummaryPDF(data, options) {
  const { eventsSummary, meetName, meetDate, meetTotalsAllClubs } = data

  const doc = createLetterPdf()
  const margin = 10
  const contentWidth = doc.internal.pageSize.getWidth() - margin * 2
  const requestedColumns = options && typeof options.columns === "number" ? options.columns : 2
  const columns = requestedColumns === 3 ? 3 : 2
  const columnGap = 5
  const columnWidth = (contentWidth - (columns - 1) * columnGap) / columns
  const isThreeColumnLayout = columns === 3
  const eventHeaderFontSize = isThreeColumnLayout ? 8 : 10
  const bodyFontSize = isThreeColumnLayout ? 7 : 8
  const columnStartY = 50
  let currentColumn = 0
  let yPos = 30

  // Aggregate totals for the clubs included in this report (based on
  // eventsSummary, which already reflects any club filters). Exhibition
  // entries are *only* counted as exhibition, not as individual.
  let reportIndividualEntries = 0
  let reportRelayEntries = 0
  let reportExhibitionEntries = 0
  let reportExhibitionRelayEntries = 0

  ;(eventsSummary || []).forEach((event) => {
    const isRelay = event.is_relay === 1
    const totalEntries = event.totalEntries || 0
    const totalExhibitionEntries = !isRelay ? event.totalExhibitionEntries || 0 : 0
    const totalExhibitionRelayTeams = isRelay ? event.totalExhibitionRelayTeams || 0 : 0
    const totalNonExhibitionEntries = Math.max(totalEntries - totalExhibitionEntries - totalExhibitionRelayTeams, 0)

    if (isRelay) {
      reportRelayEntries += totalNonExhibitionEntries
      reportExhibitionRelayEntries += totalExhibitionRelayTeams
    } else {
      reportIndividualEntries += totalNonExhibitionEntries
      reportExhibitionEntries += totalExhibitionEntries
    }
  })

  // Build team-by-team totals for the clubs in this report so we can show a
  // compact summary near the top of the PDF.
  const clubTotalsMap = new Map()

  ;(eventsSummary || []).forEach((event) => {
    const isRelay = event.is_relay === 1
    const clubs = event.clubs || []

    clubs.forEach((club) => {
      const clubId = club.clubId
      if (clubId == null) return

      if (!clubTotalsMap.has(clubId)) {
        const clubName = club.clubName || `Club ${clubId}`
        clubTotalsMap.set(clubId, {
          clubId,
          clubName,
          individualEntries: 0,
          relayEntries: 0,
          exhibitionEntries: 0,
          exhibitionRelayTeams: 0,
        })
      }

      const agg = clubTotalsMap.get(clubId)
      const clubTotalEntries = club.entries || 0
      const clubExhibitionEntries = !isRelay ? club.exhibitionEntries || 0 : 0
      const clubExhibitionRelayTeams = isRelay ? club.exhibitionRelayTeams || 0 : 0

      if (isRelay) {
        agg.relayEntries += Math.max(clubTotalEntries - clubExhibitionRelayTeams, 0)
        agg.exhibitionRelayTeams += clubExhibitionRelayTeams
      } else {
        const clubNonExhibitionEntries = Math.max(
          clubTotalEntries - clubExhibitionEntries,
          0,
        )
        agg.individualEntries += clubNonExhibitionEntries
        agg.exhibitionEntries += clubExhibitionEntries
      }
    })
  })

  const clubTotals = Array.from(clubTotalsMap.values()).sort((a, b) =>
    a.clubName.localeCompare(b.clubName),
  )

  const drawHeader = () => {
    doc.setFont("MuseoSansRounded-500", "normal")
    doc.setFontSize(12)

    drawReportLogo(
      doc,
      options?.logoDataUri || null,
      options?.logoWidth || null,
      options?.logoHeight || null,
      margin + contentWidth - 28,
      6,
      28,
      14,
    )

    const title = meetName || "Meet Entry Summary"
    const titleWidth = doc.getTextWidth(title)
    const titleX = margin + (contentWidth - titleWidth) / 2
    doc.text(title, titleX, 14)

    if (meetDate) {
      const dateText = String(meetDate).replace(/\//g, "-")
      const dateWidth = doc.getTextWidth(dateText)
      const dateX = margin + (contentWidth - dateWidth) / 2
      doc.text(dateText, dateX, 20)
    }

    doc.setFontSize(9)
    const subtitle = "Meet Entry Summary"
    const subtitleWidth = doc.getTextWidth(subtitle)
    const subtitleX = margin + (contentWidth - subtitleWidth) / 2
    doc.text(subtitle, subtitleX, 26)

    doc.setFontSize(7)
    doc.text("Powered by Swimmingly®", margin, 8)
  }

  const drawSummaryBlock = () => {
    const globalTotals = meetTotalsAllClubs || {
      individualEntries: reportIndividualEntries,
      relayEntries: reportRelayEntries,
      exhibitionEntries: reportExhibitionEntries,
      exhibitionRelayEntries: reportExhibitionRelayEntries,
    }

    doc.setFont("MuseoSansRounded-500", "normal")
    doc.setFontSize(8)

    const lineA = `Total - Individual: ${globalTotals.individualEntries}  Relay: ${globalTotals.relayEntries}  Ex. Ind: ${globalTotals.exhibitionEntries}  Ex. Relay: ${globalTotals.exhibitionRelayEntries || 0}`
    const lineAWidth = doc.getTextWidth(lineA)
    const lineAX = margin + (contentWidth - lineAWidth) / 2
    doc.text(lineA, lineAX, yPos)
    yPos += 5

    const lineB = `Clubs in Report - Individual: ${reportIndividualEntries}  Relay: ${reportRelayEntries}  Ex. Ind: ${reportExhibitionEntries}  Ex. Relay: ${reportExhibitionRelayEntries}`
    const lineBWidth = doc.getTextWidth(lineB)
    const lineBX = margin + (contentWidth - lineBWidth) / 2
    doc.text(lineB, lineBX, yPos)
    yPos += 8

    if (clubTotals.length > 0) {
      const sections = [
        {
          key: "individualEntries",
          heading: "Individual Entries",
        },
        {
          key: "relayEntries",
          heading: "Relay Entries",
        },
        {
          key: "exhibitionEntries",
          heading: "Exhibition Entries (Individual)",
        },
        {
          key: "exhibitionRelayTeams",
          heading: "Exhibition Entries (Relay)",
        },
      ]

      sections.forEach((section) => {
        const key = section.key
        const heading = section.heading

        const hasAny = clubTotals.some((c) => (c[key] || 0) > 0)
        if (!hasAny) {
          return
        }

        // Heading in Museo 700 with an underline sized to text width.
        doc.setFont("MuseoSansRounded-700", "normal")
        doc.setFontSize(8)
        doc.text(heading, margin, yPos)
        const headingWidth = doc.getTextWidth(heading)
        doc.setLineWidth(0.2)
        doc.line(margin, yPos + 0.8, margin + headingWidth, yPos + 0.8)
        yPos += 4

        // Club lines in Museo 500, slightly smaller, left-aligned with
        // right-aligned numbers so digits line up vertically.
        doc.setFont("MuseoSansRounded-500", "normal")
        doc.setFontSize(7)

        clubTotals.forEach((club) => {
          const value = String(club[key] || 0)
          const label = `${club.clubName}:`
          doc.text(label, margin, yPos)

          const valueWidth = doc.getTextWidth(value)
          const valueXRight = margin + columnWidth
          const valueX = valueXRight - valueWidth
          doc.text(value, valueX, yPos)

          yPos += 4
        })

        yPos += 4
      })
    }

    // Ensure we start the multi-column event body far enough below the
    // summary block for readability.
    if (yPos < columnStartY) {
      yPos = columnStartY
    }
  }

  const checkPageHeight = (requiredSpace) => {
    const maxY = 280
    if (yPos + requiredSpace <= maxY) return

    // Move to the next column; if out of columns, add a new page.
    yPos = columnStartY
    currentColumn += 1
    if (currentColumn >= columns) {
      doc.addPage()
      drawHeader()
      currentColumn = 0
      yPos = columnStartY
    }
  }

  drawHeader()
  drawSummaryBlock()

  const sortedEvents = (eventsSummary || []).slice().sort((a, b) => {
    const aNum = a.event_numb || 0
    const bNum = b.event_numb || 0
    return aNum - bNum
  })

  sortedEvents.forEach((event) => {
    const isRelay = event.is_relay === 1

    const genderLabel = formatGenderLabel(event.event_gender)
    const distance = event.distance || ""
    const course = event.course || ""
    const stroke = event.stroke || ""
    const ageGroup = event.age_group || ""
    const rawEventNumber = event.event_numb || ""
    const eventNumber = (event.display_number && event.display_number !== String(rawEventNumber)) ? event.display_number : rawEventNumber
    const divSuffix = event.division ? ` [${event.division}]` : ""
    const eventNumberStr = eventNumber === "" ? "" : String(eventNumber)
    const eventNumberFormatted = eventNumberStr
      ? eventNumberStr.padStart(2, "0")
      : ""

    const lineHeight = 5
    const clubsCount = (event.clubs || []).length
    // Build the event header text and wrap it within the column width to
    // prevent it from bleeding into neighboring columns (especially in 3-col).
    const prefix = `Event ${eventNumberFormatted}${divSuffix}: `
    const mainText = `${ageGroup} ${genderLabel} ${distance}${course} ${stroke}`.trim()
    const line1 = `${prefix}${mainText}`
    const headerLines = doc.splitTextToSize(line1, columnWidth)

    const neededSpace = headerLines.length * lineHeight + 1 * lineHeight + clubsCount * lineHeight + 4
    checkPageHeight(neededSpace)

    const columnStartX = margin + currentColumn * (columnWidth + columnGap)

    // Event header in Museo Sans Rounded 700; slightly smaller in 3-col layout
    doc.setFontSize(eventHeaderFontSize)
    doc.setFont("MuseoSansRounded-700", "normal")
    doc.text(headerLines, columnStartX, yPos)
    yPos += headerLines.length * lineHeight

    // Body text in Museo Sans Rounded 500; scaled a bit smaller in 3-col
    doc.setFontSize(bodyFontSize)
    doc.setFont("MuseoSansRounded-500", "normal")

    const totalEntries = event.totalEntries || 0
    const totalExhibitionEntries = !isRelay ? event.totalExhibitionEntries || 0 : 0
    const totalExhibitionRelayTeams = isRelay ? event.totalExhibitionRelayTeams || 0 : 0
    const totalNonExhibitionEntries = Math.max(totalEntries - totalExhibitionEntries - totalExhibitionRelayTeams, 0)

    const totalParts = [`Total entries: ${totalNonExhibitionEntries}`]
    if (!isRelay && totalExhibitionEntries > 0) {
      totalParts.push(`Ex. Ind: ${totalExhibitionEntries}`)
    }
    if (isRelay && totalExhibitionRelayTeams > 0) {
      totalParts.push(`Ex. Relay: ${totalExhibitionRelayTeams}`)
    }
    const totalLine = totalParts.join("   ")
    doc.text(totalLine, columnStartX, yPos)
    yPos += lineHeight

    const clubs = event.clubs || []

    // Compute max label width (within this column) so entry counts align vertically
    let maxLabelWidth = 0
    clubs.forEach((club) => {
      const clubName = club.clubName || `Club ${club.clubId}`
      const label = `  ${clubName}:`
      const w = doc.getTextWidth(label)
      if (w > maxLabelWidth) maxLabelWidth = w
    })
    const numberX = columnStartX + maxLabelWidth + 2
    const clubLineHeight = 4

    clubs.forEach((club) => {
      const clubName = club.clubName || `Club ${club.clubId}`
      const label = `  ${clubName}:`
      doc.text(label, columnStartX, yPos)

      const clubTotalEntries = club.entries || 0
      const clubExhibitionEntries = !isRelay ? club.exhibitionEntries || 0 : 0
      const clubExhibitionRelayTeams = isRelay ? club.exhibitionRelayTeams || 0 : 0
      const clubNonExhibitionEntries = Math.max(clubTotalEntries - clubExhibitionEntries - clubExhibitionRelayTeams, 0)

      let entriesText = `${clubNonExhibitionEntries} entries`
      if (!isRelay && clubExhibitionEntries > 0) {
        entriesText += ` (${clubExhibitionEntries} ex. ind)`
      }
      if (isRelay && clubExhibitionRelayTeams > 0) {
        entriesText += ` (${clubExhibitionRelayTeams} ex. relay)`
      }

      doc.text(entriesText, numberX, yPos)
      yPos += clubLineHeight
    })

    yPos += 2
  })

  const fileName = formatFileName(meetName, meetDate)

  const pageCount = doc.getNumberOfPages()
  doc.setFontSize(8)
  doc.setFont("MuseoSansRounded-500", "normal")
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i)
    const pageLabel = `${i} of ${pageCount}`
    const pageWidthLocal = doc.internal.pageSize.getWidth()
    const pageHeightLocal = doc.internal.pageSize.getHeight()
    const x = pageWidthLocal - doc.getTextWidth(pageLabel) - 10
    const y = pageHeightLocal - 8
    doc.text(pageLabel, x, y)
  }

  const blobUrl = doc.output("bloburl")
  if (!options || options.mode !== "previewOnly") {
    doc.save(fileName)
  }

  return blobUrl
}
